import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AppShell } from "@/components/app/AppShell";

import Landing from "@/pages/marketing/Landing";
import Product from "@/pages/marketing/Product";
import HowItWorks from "@/pages/marketing/HowItWorks";
import Pricing from "@/pages/marketing/Pricing";
import Security from "@/pages/marketing/Security";
import About from "@/pages/marketing/About";
import LearnPublic from "@/pages/marketing/LearnPublic";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/app/Dashboard";
import DailyBrief from "@/pages/app/DailyBrief";
import Journal from "@/pages/app/Journal";
import Analytics from "@/pages/app/Analytics";
import Backtest from "@/pages/app/Backtest";
import AskAurum from "@/pages/app/AskAurum";
import Coach from "@/pages/app/Coach";
import StrategyLab from "@/pages/app/StrategyLab";
import LearnCenter from "@/pages/app/LearnCenter";
import SettingsPage from "@/pages/app/Settings";
import Admin from "@/pages/app/Admin";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { Terms, Privacy, Refund } from "@/pages/marketing/LegalPages";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
      <div className="h-10 w-10 rounded-full border-2 border-aurum border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ children, requireOnboard = true }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (requireOnboard && !user.onboarded && user.role !== "admin")
    return <Navigate to="/onboarding" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/product" element={<Product />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/security" element={<Security />} />
      <Route path="/about" element={<About />} />
      <Route path="/learn" element={<LearnPublic />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refunds" element={<Refund />} />
      <Route path="/onboarding" element={<Protected requireOnboard={false}><Onboarding /></Protected>} />

      <Route path="/app" element={<Protected><AppShell><Dashboard /></AppShell></Protected>} />
      <Route path="/app/brief" element={<Protected><AppShell><DailyBrief /></AppShell></Protected>} />
      <Route path="/app/journal" element={<Protected><AppShell><Journal /></AppShell></Protected>} />
      <Route path="/app/analytics" element={<Protected><AppShell><Analytics /></AppShell></Protected>} />
      <Route path="/app/backtest" element={<Protected><AppShell><Backtest /></AppShell></Protected>} />
      <Route path="/app/ask" element={<Protected><AppShell><AskAurum /></AppShell></Protected>} />
      <Route path="/app/coach" element={<Protected><AppShell><Coach /></AppShell></Protected>} />
      <Route path="/app/strategy" element={<Protected><AppShell><StrategyLab /></AppShell></Protected>} />
      <Route path="/app/learn" element={<Protected><AppShell><LearnCenter /></AppShell></Protected>} />
      <Route path="/app/settings" element={<Protected><AppShell><SettingsPage /></AppShell></Protected>} />
      <Route path="/app/admin" element={<Protected><AppShell><Admin /></AppShell></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster theme="dark" position="bottom-right" richColors closeButton duration={3500} />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
